import { useState, useEffect } from "react";
import {
  FileText, Users, BookOpen, Shield, CheckCircle2, Clock,
  AlertTriangle, TrendingUp, Building2, Download,
  Sparkles, Loader2, BarChart3, GraduationCap, Filter,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ec4899"];

const REPORT_TYPES = [
  { id: "overview", label: "Overview", icon: BarChart3, desc: "Platform-wide summary metrics" },
  { id: "enrollment", label: "Enrollment", icon: BookOpen, desc: "Course enrollment & completion data" },
  { id: "compliance", label: "Compliance", icon: Shield, desc: "Compliance programs & deadlines" },
  { id: "performance", label: "Performance", icon: GraduationCap, desc: "Assessment scores & pass rates" },
  { id: "tenant", label: "By Tenant", icon: Building2, desc: "Per-tenant performance reports" },
];

const KPI = ({ label, value, sub, icon: Icon, color }) => (
  <Card className="border shadow-none">
    <CardContent className="p-4">
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center mb-2", color)}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const ReportsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState("overview");
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/enhanced");
        setData(res.data);
      } catch { toast.error("Failed to load reports"); }
      setLoading(false);
    })();
  }, []);

  const handleExport = async (section) => {
    try {
      const res = await api.get(`/reports/export/csv?section=${section}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `report_${section}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Report downloaded");
    } catch { toast.error("Export failed"); }
  };

  const fetchAI = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      const res = await api.post("/analytics/ai-insight", { section: `report_${activeReport}`, metrics: data.overview });
      setAiSummary(res.data.insight);
    } catch { toast.error("AI failed"); }
    setAiLoading(false);
  };

  if (loading) return (
    <AdminLayout title="Reports">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return null;
  const o = data.overview || {};

  return (
    <AdminLayout title="Reports">
      <div className="space-y-5 max-w-[1400px]" data-testid="reports-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold">Reports</h1>
            <p className="text-sm text-muted-foreground">Enterprise reporting — exportable data tables & insights</p>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={fetchAI} disabled={aiLoading} data-testid="ai-report-btn">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Summary
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => handleExport(activeReport)} data-testid="export-report-btn">
              <Download className="w-3 h-3" /> Export CSV
            </Button>
          </div>
        </div>

        {/* AI Summary */}
        {aiSummary && (
          <Card className="border-2 border-violet-200 bg-violet-50/30 shadow-none" data-testid="ai-report-summary">
            <CardContent className="p-4"><div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /><div className="text-sm whitespace-pre-line">{aiSummary}</div></div></CardContent>
          </Card>
        )}

        {/* Report Type Selector */}
        <div className="flex items-center gap-1.5 border-b overflow-x-auto">
          {REPORT_TYPES.map(r => (
            <button key={r.id} onClick={() => { setActiveReport(r.id); setAiSummary(null); }}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 transition-colors",
                activeReport === r.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`report-tab-${r.id}`}>
              <r.icon className="w-3.5 h-3.5" /> {r.label}
            </button>
          ))}
        </div>

        {/* Overview Report */}
        {activeReport === "overview" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <KPI label="Total Users" value={o.total_users} icon={Users} color="text-blue-600 bg-blue-50" sub={`${o.total_learners} learners`} />
              <KPI label="Enrollments" value={o.total_enrollments} icon={BookOpen} color="text-violet-600 bg-violet-50" />
              <KPI label="Completion Rate" value={`${o.completion_rate}%`} icon={CheckCircle2} color="text-green-600 bg-green-50" sub={`${o.total_completions} completed`} />
              <KPI label="Compliance Rate" value={`${o.compliance_rate}%`} icon={Shield} color={o.overdue_compliance > 0 ? "text-red-600 bg-red-50" : "text-teal-600 bg-teal-50"} sub={`${o.overdue_compliance} overdue`} />
              <KPI label="Assessment Pass Rate" value={`${o.assessment_pass_rate}%`} icon={GraduationCap} color="text-amber-600 bg-amber-50" sub={`Avg: ${o.avg_assessment_score}%`} />
            </div>

            {/* Monthly Trend */}
            <Card className="border shadow-none" data-testid="report-trend">
              <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
              <CardContent className="px-2 pb-3">
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={data.monthly_trend}>
                    <defs>
                      <linearGradient id="enrG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                      <linearGradient id="compG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} /><stop offset="95%" stopColor="#22c55e" stopOpacity={0} /></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={35} />
                    <Tooltip />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="enrollments" stroke="#3b82f6" fill="url(#enrG)" strokeWidth={2} name="Enrollments" />
                    <Area type="monotone" dataKey="completions" stroke="#22c55e" fill="url(#compG)" strokeWidth={2} name="Completions" />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Enrollment Report */}
        {activeReport === "enrollment" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPI label="Total Enrollments" value={o.total_enrollments} icon={BookOpen} color="text-blue-600 bg-blue-50" />
              <KPI label="Completions" value={o.total_completions} icon={CheckCircle2} color="text-green-600 bg-green-50" />
              <KPI label="Completion Rate" value={`${o.completion_rate}%`} icon={TrendingUp} color="text-violet-600 bg-violet-50" />
              <KPI label="ILT Sessions" value={o.total_sessions || 0} icon={Users} color="text-amber-600 bg-amber-50" />
            </div>
            <Card className="border shadow-none" data-testid="dept-enrollment">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Department Enrollment Report</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      {["Department", "Learners", "Enrolled", "Completed", "Rate"].map(h => (
                        <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.department_breakdown?.map(d => (
                        <tr key={d.department} className="border-b hover:bg-muted/20">
                          <td className="p-2 text-sm font-medium">{d.department}</td>
                          <td className="p-2 text-sm">{d.learners}</td>
                          <td className="p-2 text-sm">{d.courses_total}</td>
                          <td className="p-2 text-sm">{d.courses_completed}</td>
                          <td className="p-2"><Badge className={cn("text-[9px]", d.completion_rate >= 70 ? "bg-green-50 text-green-700" : d.completion_rate >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>{d.completion_rate}%</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Enrollments by Department</CardTitle></CardHeader>
              <CardContent className="px-2 pb-3">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.department_breakdown}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="department" tick={{ fontSize: 9 }} />
                    <YAxis tick={{ fontSize: 10 }} width={35} />
                    <Tooltip />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="courses_total" fill="#3b82f6" name="Enrolled" barSize={18} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="courses_completed" fill="#22c55e" name="Completed" barSize={18} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Compliance Report */}
        {activeReport === "compliance" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPI label="Compliance Rate" value={`${o.compliance_rate}%`} icon={Shield} color="text-teal-600 bg-teal-50" />
              <KPI label="Overdue" value={o.overdue_compliance} icon={AlertTriangle} color="text-red-600 bg-red-50" />
              <KPI label="Total Users" value={o.total_users} icon={Users} color="text-blue-600 bg-blue-50" />
              <KPI label="Completion Rate" value={`${o.completion_rate}%`} icon={CheckCircle2} color="text-green-600 bg-green-50" />
            </div>
            <Card className="border shadow-none" data-testid="compliance-report-table">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Compliance by Department</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      {["Department", "Learners", "Enrolled", "Completed", "Rate"].map(h => (
                        <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.department_breakdown?.map(d => (
                        <tr key={d.department} className="border-b hover:bg-muted/20">
                          <td className="p-2 text-sm font-medium">{d.department}</td>
                          <td className="p-2 text-sm">{d.learners}</td>
                          <td className="p-2 text-sm">{d.courses_total}</td>
                          <td className="p-2 text-sm">{d.courses_completed}</td>
                          <td className="p-2"><Badge className={cn("text-[9px]", d.completion_rate >= 80 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700")}>{d.completion_rate}%</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Performance Report */}
        {activeReport === "performance" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <KPI label="Assessment Pass Rate" value={`${o.assessment_pass_rate}%`} icon={GraduationCap} color="text-green-600 bg-green-50" />
              <KPI label="Avg Score" value={`${o.avg_assessment_score}%`} icon={TrendingUp} color="text-blue-600 bg-blue-50" />
              <KPI label="Completion Rate" value={`${o.completion_rate}%`} icon={CheckCircle2} color="text-violet-600 bg-violet-50" />
              <KPI label="Total Learners" value={o.total_learners} icon={Users} color="text-amber-600 bg-amber-50" />
            </div>
            <Card className="border shadow-none" data-testid="performance-dept-table">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Performance by Department</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      {["Department", "Learners", "Enrolled", "Completed", "Rate"].map(h => (
                        <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.department_breakdown?.map(d => (
                        <tr key={d.department} className="border-b hover:bg-muted/20">
                          <td className="p-2 text-sm font-medium">{d.department}</td>
                          <td className="p-2 text-sm">{d.learners}</td>
                          <td className="p-2 text-sm">{d.courses_total}</td>
                          <td className="p-2 text-sm">{d.courses_completed}</td>
                          <td className="p-2"><Badge className={cn("text-[9px]", d.completion_rate >= 70 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}>{d.completion_rate}%</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tenant Report */}
        {activeReport === "tenant" && data.tenant_reports?.length > 0 && (
          <div className="space-y-5">
            <Card className="border shadow-none" data-testid="tenant-report-table">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Performance by Tenant</CardTitle></CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead><tr className="border-b bg-muted/30">
                      {["Tenant", "Learners", "Enrollments", "Completions", "Rate", "Compliance", "Hours"].map(h => (
                        <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {data.tenant_reports.map(t => (
                        <tr key={t.tenant_id} className="border-b hover:bg-muted/20">
                          <td className="p-2 text-sm font-medium">{t.tenant_name}</td>
                          <td className="p-2 text-sm">{t.learners}</td>
                          <td className="p-2 text-sm">{t.enrollments}</td>
                          <td className="p-2 text-sm">{t.completions}</td>
                          <td className="p-2"><Badge className={cn("text-[9px]", t.completion_rate >= 70 ? "bg-green-50 text-green-700" : t.completion_rate >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>{t.completion_rate}%</Badge></td>
                          <td className="p-2"><Badge className={cn("text-[9px]", t.compliance_rate >= 80 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}>{t.compliance_rate}%</Badge></td>
                          <td className="p-2 text-sm">{t.learning_hours}h</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Tenant Completion Comparison</CardTitle></CardHeader>
              <CardContent className="px-2 pb-3">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.tenant_reports}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="tenant_name" tick={{ fontSize: 8 }} angle={-15} />
                    <YAxis tick={{ fontSize: 10 }} width={35} />
                    <Tooltip />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="completions" fill="#22c55e" name="Completions" barSize={16} radius={[3, 3, 0, 0]} />
                    <Bar dataKey="enrollments" fill="#3b82f6" name="Enrollments" barSize={16} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        {activeReport === "tenant" && (!data.tenant_reports || data.tenant_reports.length === 0) && (
          <Card className="border shadow-none"><CardContent className="p-8 text-center text-sm text-muted-foreground">Tenant reports available for Super Admin role only.</CardContent></Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default ReportsPage;
